import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import { COMMENTABLE_TYPES, GOAL_LOCAL_OFFERS, CAMPAIGN_TYPES, NOTIFICATION_EVENTS, CREATIVE_STATUSES } from '@/util/constants';
import { adapi, Loading, Debug, storeGetters, Timeline, CreativePreview, NotFound, Comment, permissions } from '~';
import CustomerInfo from '@/components/customer-info/CustomerInfo.vue';
import CampaignInfo from '@/components/campaign-info/CampaignInfo.vue';
import AssigneesInfo from '@/components/assignees-info/AssigneesInfo.vue';
import AdvBuilder from '@/components/adv-builder/AdvBuilder.vue';
import CreativeStatus from '@/components/lab/creative-status/CreativeStatus.vue';
import CreativeHeading from '@/components/lab/creative-heading/CreativeHeading.vue';
import OfferBrief from '@/components/offer-brief/OfferBrief.vue';
import CreativeExternalPreviews from '@/components/lab/creative-external-previews/CreativeExternalPreviews.vue';
import TriggerNotification from '@/components/trigger-notification/TriggerNotification.vue';
import SingleCommentBox from '@/components/lab/single-comment-box/SingleCommentBox.vue';

const debug = Debug('creative');
export default {
  components: {
    Comment,
    NotFound,
    AssigneesInfo,
    TriggerNotification,
    loading: Loading,
    timeline: Timeline,
    'customer-info': CustomerInfo,
    'campaign-info': CampaignInfo,
    'adv-builder': AdvBuilder,
    'creative-preview': CreativePreview,
    'creative-status': CreativeStatus,
    'creative-heading': CreativeHeading,
    'offer-brief': OfferBrief,
    'creative-external-previews': CreativeExternalPreviews,
    'single-comment-box': SingleCommentBox,
  },
  data() {
    return {
      addingComment: false,
      activatingCreative: false,
      creative_external_previews: [],
      NOTIFICATION_EVENTS,
    };
  },
  computed: {
    ...mapState('creative', {
      creative: 'current',
      loading: 'loading',
      error: 'error',
    }),
    ...mapState('auth', ['user']),
    ...mapState('interests', {
      loadingInterests: 'loading',
    }),
    campaign() {
      const c = _.get(this.creative, 'campaign', null);
      if (c) {
        this.setCurrentCampaign(c);
      }
      return c;
    },
    customer() {
      return _.get(this.campaign, 'customer', null);
    },
    permissions() {
      return permissions.getPermissions(this.creative);
    },
    comment() {
      return {
        commentable_id: this.creative.id,
        commentable_type: COMMENTABLE_TYPES.CREATIVE,
      };
    },
    landingPage() {
      return _.get(this.campaign, 'landingpages.[0]', null);
    },
    campaignFields() {
      const fields = [
        'campaign.name', 'campaign.code', 'initial-start-date', 'status', 'facebook-page',
        'landing-page', 'campaign.creator_email', 'target.language', 'objective',
        'campaign_iteration', 'goal_type', 'target.locations', 'target.age-gender',
        'target.interests', 'target.placement', 'duplicate_of',
      ];
      if (_.get(this.campaign, 'lead_ads_enabled', false)) {
        fields.push('lead-data');
      }
      return fields;
    },
    canViewTimeline() {
      return permissions.canGlobal(this.user, 'Creative', ['viewTimeline']);
    },
    timelineItems() {
      let items = _.get(this, 'creative.timeline', []);
      const agentBrief = _.get(this, 'campaign.comments', [])
        .filter(i => i.type === 'brief')[0] || null;

      if (agentBrief) {
        items = [...items, { type: 'Comment', data: agentBrief }];
      }

      return items;
    },
    canMoveFromOnboarding() {
      return this.permissions.includes('moveForward')
        && _.get(this, 'creative.status') === CREATIVE_STATUSES.ONBOARDING;
    },
    lastBrief() {
      return this.timelineItems
        .filter(i => i.type === 'Comment')
        .filter(i => i.data.type === 'brief' || i.data.type === 'rejection')
        .find(() => true);
    },
  },
  methods: {
    ...mapActions('interests', ['getInterests']),
    ...mapActions('creative', ['getCreative', 'getCallToActionList', 'completeCreative', 'reloadCreative']),
    ...mapActions('campaign', ['setCurrentCampaign', 'reloadCampaign']),
    ...mapActions('genericConfig', {
      updateGenericConfig: 'updateConfig',
    }),
    updateComment() {
      this.addingComment = true;
      return this.$refs.comment.updateComment()
        .then(() => {
          this.$toast.success({ message: this.$t('pos.comment.comment-added') });
          this.addingComment = false;
        })
        .then(() => this.reloadCreative())
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          this.addingComment = false;
        });
    },
    displayPreviewUploader() {
      if (this.creative && this.creative.type === CAMPAIGN_TYPES.EXTERNAL) {
        return true;
      }
      return false;
    },
    displayAdvBuilder() {
      if (this.creative && this.creative.type !== CAMPAIGN_TYPES.EXTERNAL) {
        return true;
      }
      return false;
    },
    isOfferAd() {
      return this.campaign.campaign_rule.goal_type === GOAL_LOCAL_OFFERS;
    },
    getCampaignOfferConfig() {
      return _.get(this, 'creative.timeline', [])
        .filter(i => i.type === 'Comment')
        .map(i => i.data)
        .filter(i => i.type === 'brief')
        .map(i => _.get(i, 'details', [])
          .filter(d => d.name === 'offer_config')
          .map(d => d.value)
          .find(() => true),
        )
        .reverse()
        .find(() => true) || null;
    },
    activateCreative() {
      this.activatingCreative = true;
      return adapi.post(`creatives/${this.creative.id}/activate`)
        .then(() => {
          this.$toast.success({ message: this.$t('pos.creative.creative-activated') });
          this.activatingCreative = false;
        })
        .then(() => this.reloadCampaign())
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          this.activatingCreative = false;
        });
    },
    onAfterOnboardingNotification(onboarded) {
      return this.completeCreative({
        id: this.creative.id,
        body: { onboarded },
      })
        .then(() => this.$toast.success({ message: this.$t('terms.misc.completed') }));
    },
  },
  mounted() {
    debug.info('creative-mounted');
    this.getCallToActionList();
    this.getCreative(storeGetters.getRouterParams().id)
      .then(() => {
        if (this.error && ![404, 403].includes(this.error.code)) {
          this.$toast.error({ message: this.error });
        }
      });
    this.updateGenericConfig();
    this.getInterests();
  },
};



// WEBPACK FOOTER //
// ./src/components/lab/creative/Creative.js